import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  CalendarCheck2, CalendarPlus, Check, X, Loader2, Clock, CheckCircle2, XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const STATUS_CONFIG = {
  pending:  { label: "Pendiente", color: "bg-amber-100 text-amber-800", icon: Clock },
  approved: { label: "Aprobada",  color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  rejected: { label: "Rechazada", color: "bg-red-100 text-red-800", icon: XCircle },
};

export default function OpsRRHHVacaciones() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState("");
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [notes, setNotes] = useState("");
  const [requesting, setRequesting] = useState(false);

  const [requests, setRequests] = useState([]);
  const [reqLoading, setReqLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const canApprove = ["hr_admin", "corporate_admin", "branch_manager"].includes(user?.role);

  useEffect(() => {
    api.get("/ops/employees?limit=200&status=alta").then(r => setEmployees(r.data.employees || [])).catch(() => {});
  }, []);

  const loadRequests = useCallback(async () => {
    setReqLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      const r = await api.get(`/ops/vacations/requests?${params}`);
      setRequests(r.data || []);
    } catch {
      toast.error("Error al cargar solicitudes");
    } finally {
      setReqLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const loadBalance = async (empId) => {
    setSelectedEmp(empId);
    setBalance(null);
    if (!empId) return;
    setBalanceLoading(true);
    try {
      const r = await api.get(`/ops/vacations/balance/${empId}`);
      setBalance(r.data);
    } catch {
      toast.error("No se pudo cargar el balance");
    } finally {
      setBalanceLoading(false);
    }
  };

  const handleRequest = async () => {
    if (!selectedEmp || !fromDate || !toDate) { toast.error("Completa colaborador y fechas"); return; }
    setRequesting(true);
    try {
      await api.post("/ops/vacations/requests", { employee_id: selectedEmp, from_date: fromDate, to_date: toDate, notes: notes || null });
      toast.success("Solicitud creada");
      setFromDate(""); setToDate(""); setNotes("");
      await loadRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear la solicitud");
    } finally {
      setRequesting(false);
    }
  };

  const handleApprove = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/ops/vacations/requests/${id}/approve`);
      toast.success("Solicitud aprobada");
      await loadRequests();
      if (selectedEmp) await loadBalance(selectedEmp);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al aprobar");
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (id) => {
    if (rejectReason.trim().length < 5) { toast.error("El motivo debe tener al menos 5 caracteres"); return; }
    setBusyId(id);
    try {
      await api.post(`/ops/vacations/requests/${id}/reject`, { reason: rejectReason.trim() });
      toast.success("Solicitud rechazada");
      setRejectingId(null);
      setRejectReason("");
      await loadRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al rechazar");
    } finally {
      setBusyId(null);
    }
  };

  const empName = (id) => {
    const e = employees.find(x => x.id === id);
    return e ? `${e.first_name} ${e.last_name}` : id;
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="vacaciones-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <CalendarCheck2 className="w-6 h-6 text-[#FFDF00]" />
          Vacaciones
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Balance según antigüedad (LFT), solicitudes y aprobación.</p>
      </div>

      {/* Balance + solicitar */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <h3 className="font-semibold text-slate-700 flex items-center gap-1.5"><CalendarPlus className="w-4 h-4" /> Consultar balance / solicitar vacaciones</h3>
        <select
          data-testid="vacation-employee-select"
          className="w-full sm:w-80 h-10 border border-slate-200 rounded-md px-3 text-sm bg-white"
          value={selectedEmp}
          onChange={e => loadBalance(e.target.value)}
        >
          <option value="">Seleccionar colaborador...</option>
          {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.position})</option>)}
        </select>

        {balanceLoading && <p className="text-sm text-slate-400">Cargando balance...</p>}

        {balance && (
          <div className="grid grid-cols-3 gap-4" data-testid="vacation-balance-cards">
            <div className="p-4 rounded-xl border bg-slate-50 border-slate-200 text-center">
              <p className="text-2xl font-bold text-slate-900">{balance.days_earned}</p>
              <p className="text-xs text-slate-500 mt-1">Días ganados</p>
            </div>
            <div className="p-4 rounded-xl border bg-slate-50 border-slate-200 text-center">
              <p className="text-2xl font-bold text-slate-900">{balance.days_taken}</p>
              <p className="text-xs text-slate-500 mt-1">Días tomados</p>
            </div>
            <div className="p-4 rounded-xl border bg-emerald-50 border-emerald-200 text-center">
              <p className="text-2xl font-bold text-slate-900">{balance.days_pending}</p>
              <p className="text-xs text-slate-500 mt-1">Días disponibles</p>
            </div>
          </div>
        )}

        {selectedEmp && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-slate-100">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Desde</label>
              <input type="date" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" value={fromDate}
                onChange={e => setFromDate(e.target.value)} data-testid="vacation-from-date" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Hasta</label>
              <input type="date" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" value={toDate}
                onChange={e => setToDate(e.target.value)} data-testid="vacation-to-date" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Notas (opcional)</label>
              <input type="text" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" value={notes}
                onChange={e => setNotes(e.target.value)} data-testid="vacation-notes-input" />
            </div>
            <div className="sm:col-span-3">
              <Button data-testid="submit-vacation-request-btn" size="sm" onClick={handleRequest} disabled={requesting}
                className="gap-1.5 bg-slate-900 hover:bg-slate-800 text-white">
                {requesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarPlus className="w-3.5 h-3.5" />}
                Solicitar vacaciones
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Bandeja de solicitudes */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-700">Solicitudes</h3>
          <select
            data-testid="vacation-status-filter"
            className="h-9 text-sm border border-slate-200 rounded-md px-3 bg-white"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="pending">Pendientes</option>
            <option value="approved">Aprobadas</option>
            <option value="rejected">Rechazadas</option>
            <option value="">Todas</option>
          </select>
        </div>

        {reqLoading ? (
          <p className="text-sm text-slate-400 py-6 text-center">Cargando solicitudes...</p>
        ) : requests.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center" data-testid="empty-vacation-requests">Sin solicitudes en este filtro</p>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="vacation-requests-list">
            {requests.map(r => {
              const st = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending;
              const Icon = st.icon;
              return (
                <div key={r.id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`vacation-request-${r.id}`}>
                  <div>
                    <p className="font-medium text-slate-900">{empName(r.employee_id)}</p>
                    <p className="text-xs text-slate-500">{r.from_date} → {r.to_date} · {r.days_requested} días{r.notes && ` · ${r.notes}`}</p>
                    {r.rejection_reason && <p className="text-xs text-red-600">Motivo: {r.rejection_reason}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${st.color}`}>
                      <Icon className="w-3 h-3" /> {st.label}
                    </span>
                    {r.status === "pending" && canApprove && (
                      rejectingId === r.id ? (
                        <div className="flex items-center gap-1.5">
                          <input type="text" placeholder="Motivo de rechazo" className="text-xs border border-slate-200 rounded px-2 py-1 w-40"
                            value={rejectReason} onChange={e => setRejectReason(e.target.value)} data-testid={`reject-reason-input-${r.id}`} />
                          <button onClick={() => handleReject(r.id)} disabled={busyId === r.id}
                            className="text-xs font-semibold px-2 py-1 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                            data-testid={`confirm-reject-btn-${r.id}`}>
                            Confirmar
                          </button>
                          <button onClick={() => { setRejectingId(null); setRejectReason(""); }} className="text-xs text-slate-400">Cancelar</button>
                        </div>
                      ) : (
                        <>
                          <button onClick={() => handleApprove(r.id)} disabled={busyId === r.id}
                            className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                            data-testid={`approve-vacation-btn-${r.id}`}>
                            <Check className="w-3 h-3" /> Aprobar
                          </button>
                          <button onClick={() => setRejectingId(r.id)}
                            className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50"
                            data-testid={`reject-vacation-btn-${r.id}`}>
                            <X className="w-3 h-3" /> Rechazar
                          </button>
                        </>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
